/**
 * Hangul (Korean) Utilities for Chosung Search
 *
 * Enables searching Korean names by initial consonants (초성).
 * Example: "ㄱㅅㅎ" matches "김상호"
 *
 * @module hangulUtils
 */

// ============================================================================
// CONSTANTS
// ============================================================================

/** Korean Chosung (initial consonants) in Unicode order */
const CHOSUNG = [
  "ㄱ", "ㄲ", "ㄴ", "ㄷ", "ㄸ", "ㄹ", "ㅁ", "ㅂ", "ㅃ",
  "ㅅ", "ㅆ", "ㅇ", "ㅈ", "ㅉ", "ㅊ", "ㅋ", "ㅌ", "ㅍ", "ㅎ",
] as const;

/** Korean Jungsung (vowels) in Unicode order */
const JUNGSUNG = [
  "ㅏ", "ㅐ", "ㅑ", "ㅒ", "ㅓ", "ㅔ", "ㅕ", "ㅖ", "ㅗ", "ㅘ",
  "ㅙ", "ㅚ", "ㅛ", "ㅜ", "ㅝ", "ㅞ", "ㅟ", "ㅠ", "ㅡ", "ㅢ", "ㅣ",
] as const;

/** Korean Jongsung (final consonants) in Unicode order (first is empty) */
const JONGSUNG = [
  "", "ㄱ", "ㄲ", "ㄳ", "ㄴ", "ㄵ", "ㄶ", "ㄷ", "ㄹ", "ㄺ",
  "ㄻ", "ㄼ", "ㄽ", "ㄾ", "ㄿ", "ㅀ", "ㅁ", "ㅂ", "ㅄ", "ㅅ",
  "ㅆ", "ㅇ", "ㅈ", "ㅊ", "ㅋ", "ㅌ", "ㅍ", "ㅎ",
] as const;

/** Unicode starting point for Hangul syllables */
const HANGUL_START = 0xac00; // 가
const HANGUL_END = 0xd7a3; // 힣

/** Standalone Jamo (자모) range */
const JAMO_START = 0x3131; // ㄱ
const JAMO_END = 0x3163; // ㅣ

/** Number of Jungsung * Jongsung combinations per Chosung */
const JUNGSUNG_COUNT = 21;
const JONGSUNG_COUNT = 28;
const SYLLABLE_UNIT = JUNGSUNG_COUNT * JONGSUNG_COUNT; // 588

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface DecomposedSyllable {
  char: string;
  cho: string;
  jung: string;
  jong: string;
  isHangul: boolean;
  isJamo: boolean;
}

export interface SearchMatchResult {
  isMatch: boolean;
  matchType: "exact" | "chosung" | "partial" | "none";
  matchedIndices: number[];
}

// ============================================================================
// CORE FUNCTIONS
// ============================================================================

/**
 * Check if a character is a complete Hangul syllable (가-힣)
 */
export function isHangulSyllable(char: string): boolean {
  const code = char.charCodeAt(0);
  return code >= HANGUL_START && code <= HANGUL_END;
}

/**
 * Check if a character is a standalone Jamo (ㄱ-ㅎ, ㅏ-ㅣ)
 */
export function isJamo(char: string): boolean {
  const code = char.charCodeAt(0);
  return code >= JAMO_START && code <= JAMO_END;
}

/**
 * Check if a character is Chosung (초성: ㄱ-ㅎ)
 */
export function isChosung(char: string): boolean {
  return CHOSUNG.includes(char as typeof CHOSUNG[number]);
}

/**
 * Decompose a single Hangul syllable into Cho/Jung/Jong
 *
 * @example
 * decomposeSyllable("김") // { char: "김", cho: "ㄱ", jung: "ㅣ", jong: "ㅁ", ... }
 */
export function decomposeSyllable(char: string): DecomposedSyllable {
  const code = char.charCodeAt(0);

  // Not a Hangul syllable
  if (code < HANGUL_START || code > HANGUL_END) {
    return {
      char,
      cho: "",
      jung: "",
      jong: "",
      isHangul: false,
      isJamo: isJamo(char),
    };
  }

  const offset = code - HANGUL_START;
  const choIndex = Math.floor(offset / SYLLABLE_UNIT);
  const jungIndex = Math.floor((offset % SYLLABLE_UNIT) / JONGSUNG_COUNT);
  const jongIndex = offset % JONGSUNG_COUNT;

  return {
    char,
    cho: CHOSUNG[choIndex] || "",
    jung: JUNGSUNG[jungIndex] || "",
    jong: JONGSUNG[jongIndex] || "",
    isHangul: true,
    isJamo: false,
  };
}

/**
 * Extract Chosung (초성) from a string
 *
 * @example
 * extractChosung("김상호") // "ㄱㅅㅎ"
 * extractChosung("Fine") // "Fine"
 */
export function extractChosung(text: string): string {
  return [...text]
    .map((char) => {
      const decomposed = decomposeSyllable(char);
      if (decomposed.isHangul) {
        return decomposed.cho;
      }
      // Return original character for non-Hangul
      return char;
    })
    .join("");
}

/**
 * Get the range of Hangul syllables that start with a given Chosung
 * Used for prefix matching (e.g., "ㄱ" matches all syllables starting with ㄱ)
 *
 * @internal Exported for potential future use
 */
export function getChosungRange(cho: string): { start: number; end: number } | null {
  const choIndex = CHOSUNG.indexOf(cho as typeof CHOSUNG[number]);
  if (choIndex === -1) return null;

  const start = HANGUL_START + choIndex * SYLLABLE_UNIT;
  const end = start + SYLLABLE_UNIT - 1;

  return { start, end };
}

// ============================================================================
// SEARCH FUNCTIONS
// ============================================================================

/**
 * Check if a query matches a target string using Chosung search
 *
 * IMPORTANT: Uses PREFIX-ONLY matching to reduce noise.
 * - "ㅇ" matches "이유진" (starts with ㅇ)
 * - "ㅇ" does NOT match "강하은" (starts with ㄱ, even though ㅇ appears later)
 *
 * @param target - The string to search in (e.g., "김상호")
 * @param query - The search query (e.g., "ㄱㅅ" or "김상")
 * @returns Match result with type and matched indices
 */
export function matchChosung(target: string, query: string): SearchMatchResult {
  if (!query || !target) {
    return { isMatch: false, matchType: "none", matchedIndices: [] };
  }

  const normalizedTarget = target.toLowerCase();
  const normalizedQuery = query.toLowerCase();

  // 1. PREFIX match only (query must start at index 0)
  if (normalizedTarget.startsWith(normalizedQuery)) {
    const matchedIndices = Array.from(
      { length: query.length },
      (_, i) => i
    );
    return { isMatch: true, matchType: "exact", matchedIndices };
  }

  // 2. Chosung-only search (query is all Chosung like "ㄱㅅㅎ")
  // STRICT: Only prefix matches allowed
  const queryChars = [...query];
  const isAllChosung = queryChars.every(isChosung);

  if (isAllChosung) {
    const targetChosung = extractChosung(target);

    // PREFIX-ONLY: Must start with the query chosung
    if (targetChosung.startsWith(query)) {
      const matchedIndices = Array.from(
        { length: query.length },
        (_, i) => i
      );
      return { isMatch: true, matchType: "chosung", matchedIndices };
    }

    // Pure chosung query but doesn't match prefix → NO MATCH
    // Don't fall through to mixed search
    return { isMatch: false, matchType: "none", matchedIndices: [] };
  }

  // 3. Mixed search (partial Hangul + Chosung like "김ㅅ")
  const targetChars = [...target];
  let queryIndex = 0;
  const matchedIndices: number[] = [];

  for (let i = 0; i < targetChars.length && queryIndex < queryChars.length; i++) {
    const targetChar = targetChars[i]!;
    const queryChar = queryChars[queryIndex]!;

    // Direct character match
    if (targetChar.toLowerCase() === queryChar.toLowerCase()) {
      matchedIndices.push(i);
      queryIndex++;
      continue;
    }

    // Query char is Chosung, check if target's Chosung matches
    if (isChosung(queryChar)) {
      const decomposed = decomposeSyllable(targetChar);
      if (decomposed.isHangul && decomposed.cho === queryChar) {
        matchedIndices.push(i);
        queryIndex++;
        continue;
      }
    }

    // Check if query is incomplete syllable (e.g., "김" matches "김민준")
    if (isHangulSyllable(queryChar)) {
      const queryDecomposed = decomposeSyllable(queryChar);
      const targetDecomposed = decomposeSyllable(targetChar);

      if (
        targetDecomposed.isHangul &&
        targetDecomposed.cho === queryDecomposed.cho
      ) {
        // If query has no jongsung and target matches cho+jung
        if (
          !queryDecomposed.jong &&
          targetDecomposed.jung === queryDecomposed.jung
        ) {
          matchedIndices.push(i);
          queryIndex++;
          continue;
        }
        // Full syllable match
        if (
          targetDecomposed.jung === queryDecomposed.jung &&
          targetDecomposed.jong === queryDecomposed.jong
        ) {
          matchedIndices.push(i);
          queryIndex++;
          continue;
        }
      }
    }
  }

  if (queryIndex === queryChars.length) {
    return { isMatch: true, matchType: "partial", matchedIndices };
  }

  return { isMatch: false, matchType: "none", matchedIndices: [] };
}

/**
 * Search function optimized for student name autocomplete
 *
 * @param students - Array of students to search
 * @param query - Search query
 * @returns Sorted array with exact matches first, then chosung matches
 */
export function searchStudents<T extends { name: string }>(
  students: T[],
  query: string
): Array<T & { matchResult: SearchMatchResult }> {
  if (!query.trim()) {
    return students.map((s) => ({
      ...s,
      matchResult: { isMatch: true, matchType: "none" as const, matchedIndices: [] },
    }));
  }

  const results = students
    .map((student) => ({
      ...student,
      matchResult: matchChosung(student.name, query),
    }))
    .filter((result) => result.matchResult.isMatch);

  // Sort: exact > chosung > partial
  const priority: Record<string, number> = {
    exact: 0,
    chosung: 1,
    partial: 2,
    none: 3,
  };

  return results.sort(
    (a, b) =>
      (priority[a.matchResult.matchType] ?? 3) - (priority[b.matchResult.matchType] ?? 3)
  );
}

// ============================================================================
// EXPORTS
// ============================================================================

export { CHOSUNG, JUNGSUNG, JONGSUNG };

export default {
  isHangulSyllable,
  isJamo,
  isChosung,
  decomposeSyllable,
  extractChosung,
  matchChosung,
  searchStudents,
};
